import type {
  AllpayUserDTO,
  TravellerMembershipDTO,
} from '@/api/trafikportalen';
import { canAccessDepartmentResource } from '@/helpers/departmentPermissions';
import { isValidPhone } from '@/helpers/validation';

export interface BulkAllpayTraveller {
  id: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  memberships: TravellerMembershipDTO[];
  allpayUsers: AllpayUserDTO[];
}

export type BulkAllpaySkipReason =
  | 'inactive'
  | 'already-assigned'
  | 'no-access'
  | 'no-department'
  | 'no-phone'
  | 'ambiguous-department';

export type BulkAllpayPlan =
  | { action: 'create'; membership: TravellerMembershipDTO }
  | { action: 'update'; users: AllpayUserDTO[] }
  | { action: 'skip'; reason: BulkAllpaySkipReason };

const getEligibleCreateMemberships = (
  traveller: BulkAllpayTraveller,
): TravellerMembershipDTO[] => {
  return traveller.memberships.filter(
    (item) =>
      item.active &&
      !traveller.allpayUsers.some(
        (user) => user.department_id === item.department_id,
      ),
  );
};

const getMatchingCreateMemberships = (
  traveller: BulkAllpayTraveller,
  filteredDepartmentIds: string[],
): TravellerMembershipDTO[] => {
  const eligibleMemberships = getEligibleCreateMemberships(traveller);

  if (filteredDepartmentIds.length === 0) {
    return eligibleMemberships;
  }

  return eligibleMemberships.filter((item) =>
    filteredDepartmentIds.includes(item.department_id),
  );
};

const getMatchingAllpayUsers = (
  traveller: BulkAllpayTraveller,
  filteredDepartmentIds: string[],
): AllpayUserDTO[] => {
  if (filteredDepartmentIds.length === 0) {
    return traveller.allpayUsers;
  }

  return traveller.allpayUsers.filter((user) =>
    filteredDepartmentIds.includes(user.department_id),
  );
};

const getExistingAssignmentPlan = (
  matchingUsers: AllpayUserDTO[],
  updateExisting: boolean,
): BulkAllpayPlan | null => {
  if (matchingUsers.length === 0) {
    return null;
  }

  const matchingDepartmentIds = [
    ...new Set(matchingUsers.map((user) => user.department_id)),
  ];

  if (matchingDepartmentIds.length > 1) {
    return { action: 'skip', reason: 'ambiguous-department' };
  }

  if (!canAccessDepartmentResource(matchingDepartmentIds[0], true)) {
    return { action: 'skip', reason: 'no-access' };
  }

  if (!updateExisting) {
    return { action: 'skip', reason: 'already-assigned' };
  }

  return { action: 'update', users: matchingUsers };
};

export const getBulkAllpayPlan = (
  traveller: BulkAllpayTraveller,
  updateExisting: boolean,
  filteredDepartmentIds: string[] = [],
): BulkAllpayPlan => {
  const isInactive = !traveller.memberships.some(
    (membership) => membership.active,
  );
  if (isInactive) {
    return { action: 'skip', reason: 'inactive' };
  }

  const matchingUsers = getMatchingAllpayUsers(
    traveller,
    filteredDepartmentIds,
  );

  // Updating an existing assignment is preferred over creating allpay on
  // another eligible department. Phone is only required for new assignments.
  if (updateExisting) {
    const existingPlan = getExistingAssignmentPlan(matchingUsers, true);
    if (existingPlan) {
      return existingPlan;
    }
  }

  const matchingMemberships = getMatchingCreateMemberships(
    traveller,
    filteredDepartmentIds,
  );
  const writableMemberships = matchingMemberships.filter((item) =>
    canAccessDepartmentResource(item.department_id, true),
  );

  if (writableMemberships.length > 1) {
    return { action: 'skip', reason: 'ambiguous-department' };
  }

  const membership = writableMemberships[0];
  if (membership) {
    if (!traveller.phone || !isValidPhone(traveller.phone)) {
      return { action: 'skip', reason: 'no-phone' };
    }

    return { action: 'create', membership };
  }

  const existingPlan = getExistingAssignmentPlan(matchingUsers, false);
  if (existingPlan) {
    return existingPlan;
  }

  if (matchingMemberships.length > 0) {
    return { action: 'skip', reason: 'no-access' };
  }

  return { action: 'skip', reason: 'no-department' };
};

export const isAdditionalBulkAllpayCreate = (
  traveller: BulkAllpayTraveller,
  plan: BulkAllpayPlan,
): plan is Extract<BulkAllpayPlan, { action: 'create' }> =>
  plan.action === 'create' && traveller.allpayUsers.length > 0;

export const getBulkAllpayCreditConsumption = (
  plans: BulkAllpayPlan[],
  credit: number,
): number => {
  return plans.reduce((total, plan) => {
    if (plan.action === 'create') {
      return total + credit;
    }

    if (plan.action === 'update') {
      return (
        total +
        plan.users.reduce((sum, user) => sum + (credit - user.credit), 0)
      );
    }

    return total;
  }, 0);
};
